import React, {memo} from 'react';
import {Counter as PepsicoDsCounter} from '@pepsico-ds/ui';
import PropTypes from 'prop-types';

/**
 * Counter component aligned with ICounter props.
 *
 * @param {Object} props - The properties object.
 * @param {number|string} [props.value=0] - The current value of the counter.
 * @param {function} [props.onUpdate] - Callback invoked when the value changes.
 * @param {number} [props.minValue] - Minimum allowed value.
 * @param {number} [props.maxValue] - Maximum allowed value.
 * @param {"small"|"medium"} [props.size="medium"] - Size of the counter.
 * @param {boolean} [props.disabled=false] - If true, disables the component.
 * @param {string} [props.feedbackType] - Feedback state (e.g., info, error, success).
 * @param {string} [props.helperText] - Helper text shown under the field.
 * @param {boolean} [props.isDark] - Dark theme toggle.
 * @param {string} [props.label] - Label for the counter.
 * @param {string} [props.placeholder] - Placeholder text when empty.
 * @param {boolean} [props.showLabelIcon] - Whether to show the label icon.
 * @param {Object} [props.style] - Inline styles.
 * @param {string} [props.className] - Custom class name.
 */
function Counter(props) {
  const {
    value,
    onUpdate,
    disabled,
    minValue,
    maxValue,
    size = 'medium',
    className,
    style,
    feedbackType,
    helperText,
    isDark,
    label,
    placeholder,
    showLabelIcon
  } = props;

  return (
    <PepsicoDsCounter
      className={className}
      error={feedbackType === 'error'}
      feedbackType={feedbackType}
      helperText={helperText}
      isDark={isDark}
      label={label}
      maxValue={maxValue}
      minValue={minValue}
      placeholder={placeholder}
      showLabelIcon={!!showLabelIcon}
      size={size}
      style={style}
      value={value}
      onUpdate={onUpdate}
      disabled={disabled}
    />
  );
}

Counter.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onUpdate: PropTypes.func,
  disabled: PropTypes.bool,
  minValue: PropTypes.number,
  maxValue: PropTypes.number,
  size: PropTypes.oneOf(['small', 'medium']),
  className: PropTypes.string,
  style: PropTypes.object,
  feedbackType: PropTypes.string,
  helperText: PropTypes.string,
  isDark: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  showLabelIcon: PropTypes.bool
};

export default memo(Counter);
